import { Link } from "react-router-dom";
import * as LucideIcons from "lucide-react";
import { ChevronRight } from "lucide-react";
import SectionHeading from "@/components/site/SectionHeading";
import { SERVICES } from "@/lib/data";

export default function Services() {
  return (
    <div data-testid="services-page">
      <section className="relative h-[50vh] min-h-[400px] overflow-hidden">
        <img src="https://images.unsplash.com/photo-1519741497674-611481863552?w=2000" alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
        <div className="relative h-full max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col justify-end pb-14">
          <p className="text-xs uppercase tracking-[0.3em] text-[#ffb700] mb-4">◆ Services</p>
          <h1 className="font-display text-5xl lg:text-7xl text-white leading-[0.95]">
            Beyond the studio. <br /><span className="phoenix-text">Onto your stage.</span>
          </h1>
        </div>
      </section>

      <section className="py-24 lg:py-32 max-w-[1400px] mx-auto px-6 lg:px-10">
        <SectionHeading eyebrow="What we offer" title="Choreography." accent="Performance. Coaching." testid="services-heading" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICES.map((s, i) => {
            const Icon = LucideIcons[s.icon] || LucideIcons.Sparkles;
            return (
              <div key={i} data-testid={`service-${i}`} className="group bg-[#0a0a0a] border border-[#ff4500]/40 hover:border-[#ff4500] p-8 lg:p-10 transition-colors flex flex-col">
                <div className="w-14 h-14 flex items-center justify-center bg-[#ff4500]/10 border border-[#ff4500]/40 group-hover:bg-[#ff4500] transition-colors mb-6">
                  <Icon className="w-6 h-6 text-[#ff4500] group-hover:text-white transition-colors" strokeWidth={1.5} />
                </div>
                <h3 className="font-display text-2xl text-white mb-3">{s.title}</h3>
                <p className="text-sm text-white/60 leading-relaxed flex-1">{s.desc}</p>
                <Link to="/contact" className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-wider text-[#ff4500] font-semibold group-hover:gap-3 transition-all">
                  Enquire <ChevronRight className="w-4 h-4" />
                </Link>
              </div>
            );
          })}
        </div>
      </section>

      {/* Highlight: Wedding choreo */}
      <section className="py-24 lg:py-32 bg-[#070707] border-y border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="image-zoom border border-white/10 overflow-hidden">
            <img src="https://images.unsplash.com/photo-1519741497674-611481863552?w=1200" alt="" className="w-full h-full object-cover aspect-[4/5]" />
          </div>
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#ffb700] mb-4">◆ Signature service</p>
            <h2 className="font-display text-4xl lg:text-6xl text-white leading-[0.95]">
              Wedding<br />
              <span className="phoenix-text">choreography</span><br />
              that lasts forever.
            </h2>
            <p className="mt-6 text-base lg:text-lg text-white/70 leading-relaxed">
              From the bride's solo to a full-family sangeet production — we choreograph, rehearse, and stage your wedding moments. Sessions available across all three branches and online.
            </p>
            <ul className="mt-8 space-y-3">
              {["First Dance & Couple Solos", "Sangeet & Mehendi routines", "Bridal squad & family ensembles", "Travel-friendly online rehearsals"].map((p, i) => (
                <li key={i} className="flex items-start gap-3 text-white/80">
                  <span className="w-2 h-2 mt-2 bg-[#ff4500]" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>
            <Link to="/contact" className="inline-block mt-10">
              <button data-testid="wedding-cta-btn" className="btn-phoenix bg-[#ff4500] text-white px-8 h-14 uppercase tracking-wider text-sm font-semibold">
                Enquire about Wedding Choreo
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-24 lg:py-32 max-w-[1400px] mx-auto px-6 lg:px-10">
        <SectionHeading eyebrow="Our Process" title="From idea to" accent="ovation." testid="process-heading" />
        <div className="grid grid-cols-1 md:grid-cols-4 gap-px bg-white/5">
          {[
            { n: "01", t: "Discovery", d: "We meet, hear your vision, and understand your timeline, audience, and budget." },
            { n: "02", t: "Concept & Music", d: "Our team develops the creative direction, music edit, and choreography brief." },
            { n: "03", t: "Rehearsals", d: "Structured sessions across 2–8 weeks — in-studio or online — until it sings." },
            { n: "04", t: "Showtime", d: "We're with you on performance day for warm-up, blocking, and final notes." },
          ].map((s, i) => (
            <div key={i} data-testid={`process-${i}`} className="bg-[#0a0a0a] p-8 hover:bg-[#0f0f0f] transition-colors">
              <p className="font-display text-5xl phoenix-text">{s.n}</p>
              <h4 className="font-display text-2xl text-white mt-4">{s.t}</h4>
              <p className="text-sm text-white/60 mt-3 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
